import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CommandPalette } from '@backrs_ui/grow-ui/src/components/CommandPalette';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { useNotice } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { cohortLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import {
  cohortOutcome,
  landedUnreadableMessage,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/seatActions/cohortOutcome';
import {
  cohortSeats,
  eligibleSeatIds,
  type SeatHolder,
} from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/seatActions/memberSeats';
import {
  useGetMyInstitutionQuery,
  type InstitutionMember,
  type MemberSeat,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import {
  BATCH_LANDED,
  useAddCohortMembershipsMutation,
  useGetRosterCohortsQuery,
  useRemoveCohortMembershipsMutation,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Avatar, Box, Checkbox, type SxProps, type Theme, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './CohortMembersPalette.agentContext';

const { membersDialog: strings } = appStrings.modules.rostersAdmin;

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

export type CohortMembersMode = 'members' | 'add';

type CohortMembersPaletteProps = Readonly<{
  open: boolean;
  onClose: () => void;
  rosterId: number;
  cohortId: number;
  mode: CohortMembersMode;
}>;

const holderOf = (row: InstitutionMember): SeatHolder => ({
  id: row.id,
  name: personName(row),
  seats: row.seats,
});

export const CohortMembersPalette = ({
  open,
  onClose,
  rosterId,
  cohortId,
  mode,
}: CohortMembersPaletteProps) => {
  const notice = useNotice();
  const cohorts = useGetRosterCohortsQuery({ rosterId });
  const cohort = cohorts.data?.data.find((row) => row.id === cohortId) ?? null;

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [selected, setSelected] = useState<ReadonlyMap<number, InstitutionMember>>(new Map());

  const [addMemberships, addState] = useAddCohortMembershipsMutation();
  const [removeMemberships, removeState] = useRemoveCohortMembershipsMutation();

  const q = debouncedSearch.trim();
  const page = useGetMyInstitutionQuery(
    open
      ? {
          range: [0, PAGE_SIZE - 1] as [number, number],
          filter: {
            ...(q === '' ? null : { q }),
            ...(mode === 'add' ? { eligible_cohort_id: cohortId } : { cohort_id: [cohortId] }),
          },
        }
      : skipToken,
  );

  const people = page.data?.data ?? [];
  const total = page.data?.total ?? 0;
  const name = cohort === null ? '' : cohortLabel(cohort);
  const isWriting = addState.isLoading || removeState.isLoading;
  const isReadable = cohort !== null && !cohorts.isError;
  const shown = isReadable ? people : [];
  const readFailure = page.isError ? noticeForError(refusalProse(page.error), strings.error) : null;

  useAgentContext({
    cohortId,
    cohortName: cohort === null ? null : name,
    view: mode,
    search: q,
    people: shown,
    selectedCount: selected.size,
  });

  const onRoster = (seats: readonly MemberSeat[]): MemberSeat[] =>
    cohortSeats(seats).filter((seat) => seat.roster_id === rosterId);

  const toggle = (row: InstitutionMember) => {
    setSelected((prev) => {
      const next = new Map(prev);

      if (!next.delete(row.id)) next.set(row.id, row);

      return next;
    });
  };

  const emptyStateMessage = (() => {
    if (cohorts.isError) return noticeForError(refusalProse(cohorts.error), strings.error);
    if (cohort === null) return cohorts.isFetching ? strings.loading : strings.gone;
    if (readFailure !== null) return readFailure;
    if (q !== '') return strings.noSearchResults;

    return mode === 'add' ? strings.emptyEligible : strings.empty;
  })();

  const submit = async (kind: 'add' | 'remove') => {
    const holders = Array.from(selected.values(), holderOf);
    const rosterSeatIds = eligibleSeatIds(holders, onRoster);
    const failed = kind === 'add' ? strings.addFailed : strings.removeFailed;

    if (rosterSeatIds.length === 0) {
      notice.announce(failed, 'error');

      return;
    }

    let results;

    // Only the write is guarded: a throw from the reporting below would otherwise be
    // announced as a refusal of a write that already committed.
    try {
      results = await (kind === 'add' ? addMemberships : removeMemberships)({
        rosterId,
        cohortId,
        rosterSeatIds,
      }).unwrap();
    } catch (error) {
      const landed = landedUnreadableMessage(error);

      if (landed !== null) {
        notice.announce(landed);

        return;
      }

      notice.announce(noticeForError(refusalProse(error), failed), 'error');

      return;
    }

    const outcome = cohortOutcome({
      results,
      holders,
      eligible: onRoster,
      landedWord: kind === 'add' ? BATCH_LANDED.add : BATCH_LANDED.remove,
      cohortName: name,
      voice: 'selection',
    });

    // Only the people who went through leave the selection: the rest stay checked as
    // the listing's own record of who still needs triage.
    setSelected((prev) => {
      const next = new Map(prev);

      outcome.landedHolderIds.forEach((id) => next.delete(id));

      return next;
    });
    notice.announce(outcome.message, outcome.severity);
  };

  const actions = [
    mode === 'add'
      ? {
          id: 'rosters-admin-cohort-members-add-submit',
          label: addState.isLoading ? strings.adding : strings.addSubmit,
          hierarchy: 'Primary' as const,
          disabled: selected.size === 0 || isWriting,
          onClick: () => void submit('add'),
        }
      : {
          id: 'rosters-admin-cohort-members-remove',
          label: removeState.isLoading ? strings.removing : strings.remove,
          hierarchy: 'Primary' as const,
          disabled: selected.size === 0 || isWriting,
          onClick: () => void submit('remove'),
        },
  ];

  const footer = (
    <>
      {readFailure !== null && shown.length > 0 ? (
        <Typography
          id="rosters-admin-cohort-members-read-error"
          variant="text-sm-regular"
          role="alert"
        >
          {readFailure}
        </Typography>
      ) : null}
      {total > shown.length ? (
        <Typography
          id="rosters-admin-cohort-members-truncated"
          variant="text-sm-regular"
          role="status"
        >
          {strings.truncated(shown.length, total)}
        </Typography>
      ) : null}
      {selected.size > 0 ? (
        <Typography
          id="rosters-admin-cohort-members-selected"
          variant="text-sm-regular"
          role="status"
        >
          {strings.selected(selected.size)}
        </Typography>
      ) : null}
    </>
  );

  return (
    <CommandPalette<InstitutionMember>
      open={open}
      onClose={onClose}
      title={mode === 'add' ? strings.addTitle(name) : strings.removeTitle(name)}
      searchPlaceholder={strings.searchPlaceholder}
      searchValue={search}
      onSearchChange={setSearch}
      items={shown}
      renderItem={(row) => (
        <Box id={`rosters-admin-cohort-member-${row.id}`} sx={rowSx}>
          <Checkbox
            id={`rosters-admin-cohort-member-select-${row.id}`}
            checked={selected.has(row.id)}
            size="small"
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckIcon />}
            inputProps={{ 'aria-label': personName(row) }}
          />
          <Avatar src={row.profile_image ?? undefined} alt={personName(row)} sx={avatarSx}>
            {personInitial(row)}
          </Avatar>
          <Box sx={rowTextSx}>
            <Typography variant="text-sm-medium">{personName(row)}</Typography>
            <Typography variant="text-xs-regular" sx={rowSubSx}>
              {row.email}
            </Typography>
          </Box>
        </Box>
      )}
      getItemKey={(row) => String(row.id)}
      selectedItem={null}
      onItemSelect={toggle}
      loading={page.isFetching && people.length === 0}
      emptyStateMessage={emptyStateMessage}
      footerContent={footer}
      actions={actions}
    />
  );
};

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const avatarSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 32,
  height: 32,
  ...typography['text-sm-regular'],
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});

const rowTextSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const rowSubSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
